/**
 * Gated debug logger.
 *
 * Enabled when ANY of these is true:
 *   - URL has `?debug=1` (or `&debug=1`) on first nav this session
 *   - `localStorage.cryptarch_debug === '1'`
 *   - global flag `window.__CRYPTARCH_DEBUG__` is truthy
 *
 * Toggle at runtime with `Ctrl+Shift+D` (handled in app/main.ts).
 *
 * Zero overhead when disabled: the early return runs before any
 * argument is touched. Pass functions for expensive payloads:
 *   dlog('solver', 'input', () => buildExpensiveSnapshot());
 *
 * Scopes are typed so future greps can find every call site
 * (`dlog('wizard',` etc) without a free-text search.
 */

export type DebugScope =
  | 'wizard' // step transitions, user picks, store mutations
  | 'solver' // input snapshot, output rows count + timings
  | 'equip' // every Bungie API call + plan + result
  | 'mod-plan' // tuning + artifice + general mod placement decisions
  | 'manifest' // table loads, version mismatches, snapshot updates
  | 'bungie' // raw API responses (status, endpoint, retry)
  | 'inventory' // load, refresh, filter, paging
  | 'router' // route resolution + share-link decode
  | 'auth' // token state changes
  | 'app'; // boot, debug toggle, generic

/** Snapshot the debug-on decision once at boot so we don't re-read the URL
 *  on every dlog call. The Ctrl+Shift+D toggle updates this in-place. */
const state: { enabled: boolean } = { enabled: detectInitial() };

function detectInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('debug') === '1') {
      // Persist the URL choice so a soft-refresh keeps debug on.
      window.localStorage?.setItem('cryptarch_debug', '1');
      return true;
    }
    if (window.localStorage?.getItem('cryptarch_debug') === '1') return true;
    const w = window as unknown as { __CRYPTARCH_DEBUG__?: boolean };
    if (w.__CRYPTARCH_DEBUG__) return true;
  } catch {
    // SSR / sandboxed iframe / storage disabled — silent.
  }
  return false;
}

/** True when debug logging is active. Cheap; safe to call in hot paths. */
export function isDebug(): boolean {
  return state.enabled;
}

/** Flip the debug state at runtime. Persists the new value to
 *  localStorage so it survives page reloads. Also emits one console
 *  line so the user has visible confirmation in the devtools. */
export function setDebug(on: boolean): void {
  state.enabled = on;
  if (typeof window === 'undefined') return;
  try {
    if (on) window.localStorage?.setItem('cryptarch_debug', '1');
    else window.localStorage?.removeItem('cryptarch_debug');
  } catch {
    /* storage disabled — ignore */
  }

  console.info(`[cryptarch] debug logging ${on ? 'ENABLED' : 'disabled'}`);
}

/** Toggle debug on/off. Returns the new state. */
export function toggleDebug(): boolean {
  setDebug(!state.enabled);
  return state.enabled;
}

/** Lazy payload — pass a function when the data is expensive to build
 *  (deep-clone, JSON.stringify, etc) so it only runs when debug is on. */
type Lazy<T> = T | (() => T);
function resolve<T>(v: Lazy<T>): T {
  return typeof v === 'function' ? (v as () => T)() : v;
}

/** Log under a typed scope. Cheap no-op when debug is off. */
export function dlog(scope: DebugScope, message: string, data?: Lazy<unknown>): void {
  if (!state.enabled) return;
  const prefix = `[${scope}]`;
  if (data === undefined) {
    console.debug(prefix, message);
    return;
  }

  console.debug(prefix, message, resolve(data));
}

/** Group-style log: nests subsequent dlog calls under a labelled block
 *  in the devtools console. Caller MUST call the returned end()
 *  function. Safe when debug is off (returns a noop). */
export function dgroup(scope: DebugScope, label: string): () => void {
  if (!state.enabled) return () => {};

  console.groupCollapsed(`[${scope}] ${label}`);

  return () => console.groupEnd();
}

/** Time a block. Logs `[scope] label — Nms` when debug is on. */
export function dtime<T>(scope: DebugScope, label: string, fn: () => T): T {
  if (!state.enabled) return fn();
  const t0 = performance.now();
  try {
    return fn();
  } finally {
    console.debug(`[${scope}] ${label}`, `${(performance.now() - t0).toFixed(1)}ms`);
  }
}

/** Async variant of dtime. */
export async function dtimeAsync<T>(
  scope: DebugScope,
  label: string,
  fn: () => Promise<T>,
): Promise<T> {
  if (!state.enabled) return fn();
  const t0 = performance.now();
  try {
    return await fn();
  } finally {
    console.debug(`[${scope}] ${label}`, `${(performance.now() - t0).toFixed(1)}ms`);
  }
}
